import { navigate } from '@reach/router'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { ColorName, Colors } from '../constants'
import { handleError } from '../data/errors'
import { getWindow } from '../global'

export interface LinkProps {
  to?: string
  color?: ColorName
  href?: string | null
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
  noTab?: boolean
  tableCell?: boolean
  children: React.ReactNode
  title?: string | null
  style?: React.CSSProperties
  className?: string
  opensNewTab?: boolean
  testid?: string
}

export class Link extends React.PureComponent<LinkProps> {
  onClickBound = (e: React.MouseEvent) => {
    const { to } = this.props
    if (!e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      if (to) {
        if (to.startsWith('http')) {
          getWindow()!.open(to)
        } else {
          navigate(to).catch(handleError)
        }
      }
    }
  }

  // Decides if the <Link> should open in the same tab / a new tab
  //
  // opensInNewTab = false                        ->  same tab
  // opensInNewTab = true                         ->  new tab
  // opensInNewTab = (undefined) + internal URL   ->  same tab
  // opensInNewTab = (undefined) + external URL   ->  new tab
  private getOpensNewTab() {
    const { to, href, opensNewTab } = this.props
    const opensNewTabIsUndefined = typeof opensNewTab === 'undefined'
    const domainName = process.env.DOMAIN_NAME || 'dynasty.com'
    const dynastyUrl = `http://${domainName}/graphql`
    const isDynastyUrl = to?.startsWith(dynastyUrl) || href?.startsWith(dynastyUrl) || false

    if (opensNewTabIsUndefined && !isDynastyUrl) {
      return '_blank'
    } else {
      return opensNewTab ? '_blank' : '_self'
    }
  }

  render() {
    const { color, to, href, title, onClick, tableCell, noTab, style, children, className, testid, ...rest } =
      this.props
    const hrefUrl = href || to
    const clickHandler = onClick || (to == null ? null : this.onClickBound)
    return (
      <LinkWrap
        className={className}
        target={this.getOpensNewTab()}
        rel="noopener"
        title={title || undefined}
        color={color}
        tableCell={tableCell}
        href={hrefUrl}
        onClick={clickHandler || undefined}
        tabIndex={noTab ? -1 : undefined}
        style={style}
        data-testid={testid}
        {...rest}
      >
        {children}
      </LinkWrap>
    )
  }
}
const LinkWrap = styled.a`
  ${({ color, tableCell }: { color?: ColorName; tableCell?: boolean }) => css`
    color: ${Colors[color || 'sky']};
    cursor: pointer;
    text-decoration: none;
    transition: color 0.05s ease-in-out;
    ${tableCell && 'display: inline-block'};
    ${tableCell && 'padding: 2px'};
    ${tableCell && 'margin: -2px'};
    &:hover {
      filter: brightness(130%);
    }
    &:focus {
      outline: none;
      filter: brightness(130%);
    }
    &:active {
      filter: brightness(150%);
    }
  `};
`

interface LinkWrapProps {
  to?: string
  href?: string
  noTab?: boolean
  linkClassName?: string
}

export default function link<P>(Component: React.ComponentType<P & LinkWrapProps>) {
  return function LinkWrap(props: P & LinkWrapProps) {
    const { to, href, noTab, linkClassName } = props

    return (
      <Link className={linkClassName} to={to} href={href} noTab={noTab}>
        <Component {...props} />
      </Link>
    )
  }
}
